import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

/**
 * Maps area names to their userPreferenceTypeId.
 * Add new entries here as more areas opt into area preferences.
 */
export const AREA_PREFERENCE_TYPE_IDS: Record<string, number> = {
    sessionDetail: 22,
    registrationDetail: 23,
};

/**
 * Default preferences per area. When loading from the DB, stored values
 * are merged onto these defaults so that missing/new keys get safe values.
 */
export interface AssessmentsTabPreferences {
    subTab: number;
    hideAboutMe: boolean | null;
    keyDatesExpanded: boolean;
}

export interface SessionDetailPreferences {
    registrations: {
        subTab: number;
        showOnlyConfirmed: boolean;
    };
    assessments: AssessmentsTabPreferences & {
        uninvitedExpanded: boolean;
        uninvitedShowOnlyConfirmed: boolean;
    };
}

export interface RegistrationDetailPreferences {
    assessments: AssessmentsTabPreferences;
}

export const AREA_PREFERENCE_DEFAULTS: Record<string, any> = {
    sessionDetail: {
        registrations: {
            subTab: 0,
            showOnlyConfirmed: false,
        },
        assessments: {
            subTab: 0,
            hideAboutMe: null,
            keyDatesExpanded: true,
            uninvitedExpanded: true,
            uninvitedShowOnlyConfirmed: false,
        },
    } as SessionDetailPreferences,
    registrationDetail: {
        assessments: {
            subTab: 0,
            hideAboutMe: null,
            keyDatesExpanded: true,
        },
    } as RegistrationDetailPreferences,
};

/**
 * Deep merges source onto target. Only applies values from source
 * where the key exists in target, preserving the target's structure.
 */
export function deepMergeDefaults(defaults: any, stored: any): any {
    if (defaults == null || typeof defaults !== "object") return stored ?? defaults;
    if (stored == null || typeof stored !== "object") return { ...defaults };

    const result: any = { ...defaults };
    for (const key of Object.keys(defaults)) {
        if (key in stored) {
            if (typeof defaults[key] === "object" && defaults[key] !== null && !Array.isArray(defaults[key])) {
                result[key] = deepMergeDefaults(defaults[key], stored[key]);
            } else {
                result[key] = stored[key];
            }
        }
    }
    return result;
}

export interface AreaPreferencesState {
    areas: Record<string, Record<string, any>>;
}

const initialState: AreaPreferencesState = {
    areas: {},
};

/** Resolves a dot-path key (e.g. "registrations.showOnlyConfirmed") to its value in an object. */
function getByPath(obj: any, path: string): any {
    const parts = path.split(".");
    let current = obj;
    for (const part of parts) {
        if (current == null) return undefined;
        current = current[part];
    }
    return current;
}

/** Sets a value at a dot-path key (e.g. "registrations.showOnlyConfirmed") in an object. */
function setByPath(obj: any, path: string, value: any): void {
    const parts = path.split(".");
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        if (current[parts[i]] == null || typeof current[parts[i]] !== "object") {
            current[parts[i]] = {};
        }
        current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
}

export const areaPreferencesSlice = createSlice({
    name: "areaPreferences",
    initialState,
    reducers: {
        loadAreaPreferences: (
            state,
            action: PayloadAction<{ area: string; data: Record<string, any> }>
        ) => {
            state.areas[action.payload.area] = action.payload.data;
        },
        setAreaPreference: (
            state,
            action: PayloadAction<{ area: string; key: string; value: any }>
        ) => {
            const { area, key, value } = action.payload;
            if (!state.areas[area]) {
                state.areas[area] = {};
            }
            setByPath(state.areas[area], key, value);
        },
    },
});

export const { loadAreaPreferences, setAreaPreference } = areaPreferencesSlice.actions;

export const selectAreaPreferences = (state: RootState) => state.areaPreferences;

/** Resolves a dot-path key from an area's preferences. */
export function selectAreaPreferenceByPath(state: RootState, area: string, key: string): any {
    return getByPath(state.areaPreferences?.areas?.[area], key);
}

export default areaPreferencesSlice.reducer;
